import type { OrgMember, OrgRow } from '~/gql/organizations/organizations'
import { useMutation, useQuery } from '@apollo/client/react'
import { RoleEnum } from '@zeus/index'
import { Loader, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import {
  GQL_INVITE_ORG_MEMBER,
  GQL_ORGANIZATION_MEMBERS,
  GQL_REMOVE_ORG_MEMBER,
} from '~/gql/organizations/organizations'
import { client } from '~/helpers/apollo'

type IProps = {
  open: boolean
  onCancel: () => void
  onChanged: () => void | Promise<unknown>
  org: OrgRow
}

const MEMBER_ROLES = [RoleEnum.Admin, RoleEnum.Formateur, RoleEnum.User]

function statusVariant(status: string): 'success' | 'warning' | 'secondary' {
  if (status === 'ENABLED') return 'success'
  if (status === 'PENDING_ACTIVATION') return 'warning'
  return 'secondary'
}

function OrganizationMembersModal({ open, onCancel, onChanged, org }: IProps) {
  const { t } = useTranslation()
  const { data, loading, refetch } = useQuery(GQL_ORGANIZATION_MEMBERS, {
    client,
    skip: !open,
    fetchPolicy: 'cache-and-network',
    variables: { orgId: org.id },
  })
  const [inviteOrgMember, { loading: inviting }] = useMutation(GQL_INVITE_ORG_MEMBER, { client })
  const [removeOrgMember] = useMutation(GQL_REMOVE_ORG_MEMBER, { client })

  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [role, setRole] = useState<RoleEnum>(RoleEnum.User)
  const [confirmingId, setConfirmingId] = useState<string | null>(null)

  const members = (data?.organizationMembers ?? []) as unknown as OrgMember[]

  const refresh = async () => {
    await refetch()
    await onChanged()
  }

  const onInvite = async () => {
    if (!email.trim() || !firstName.trim() || !lastName.trim()) {
      toast.error(t('organization.member.invite.required'))
      return
    }
    try {
      await inviteOrgMember({
        variables: {
          input: {
            orgId: org.id,
            email: email.trim(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            role,
          },
        },
      })
      toast.success(t('organization.member.invite.success'))
      setEmail('')
      setFirstName('')
      setLastName('')
      setRole(RoleEnum.User)
      await refresh()
    } catch (err) {
      const msg = err instanceof Error ? err.message : ''
      toast.error(t(msg.includes('EMAIL_ALREADY_USED') ? 'organization.member.invite.emailUsed' : 'organization.member.invite.failed'))
    }
  }

  const onRemove = async (userId: string) => {
    try {
      await removeOrgMember({ variables: { orgId: org.id, userId } })
      toast.success(t('organization.member.remove.success'))
      setConfirmingId(null)
      await refresh()
    } catch {
      toast.error(t('organization.member.remove.failed'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent aria-describedby={undefined} className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('organization.members.title', { name: org.name })}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Invitation */}
          <div className="rounded-md border border-border p-3">
            <p className="mb-2 text-sm font-medium">{t('organization.member.invite.title')}</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <Input placeholder={t('common.email')} type="email" value={email} onChange={e => setEmail(e.target.value)} />
              <select
                value={role}
                onChange={e => setRole(e.target.value as RoleEnum)}
                className="h-9 rounded-md border border-border bg-transparent px-3 text-sm"
              >
                {MEMBER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <Input placeholder={t('common.firstname')} value={firstName} onChange={e => setFirstName(e.target.value)} />
              <Input placeholder={t('common.lastname')} value={lastName} onChange={e => setLastName(e.target.value)} />
            </div>
            <div className="mt-2 flex justify-end">
              <Button size="sm" onClick={onInvite} disabled={inviting}>
                {inviting ? <Loader className="mr-2 size-4 animate-spin" /> : <UserPlus className="mr-2 size-4" />}
                {t('organization.member.invite.submit')}
              </Button>
            </div>
          </div>

          {/* Liste des membres */}
          <div className="max-h-[45vh] overflow-y-auto rounded-md border">
            {loading && members.length === 0
              ? <div className="flex justify-center p-6"><Loader className="size-6 animate-spin" /></div>
              : members.length === 0
                ? <p className="p-4 text-center text-sm text-muted-foreground">{t('organization.members.empty')}</p>
                : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('common.email')}</TableHead>
                          <TableHead>{t('common.name')}</TableHead>
                          <TableHead>{t('common.role')}</TableHead>
                          <TableHead>{t('common.status')}</TableHead>
                          <TableHead className="text-right">{t('common.action')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {members.map(m => (
                          <TableRow key={m.id}>
                            <TableCell className="font-medium">{m.email}</TableCell>
                            <TableCell>
                              {m.firstName}
                              {' '}
                              {m.lastName}
                            </TableCell>
                            <TableCell>{m.role}</TableCell>
                            <TableCell>
                              <Badge variant={statusVariant(m.status)}>{m.status}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              {confirmingId === m.id
                                ? (
                                    <div className="flex justify-end gap-1">
                                      <Button variant="destructive" size="sm" onClick={() => onRemove(m.id)}>
                                        {t('common.confirm')}
                                      </Button>
                                      <Button variant="ghost" size="sm" onClick={() => setConfirmingId(null)}>
                                        {t('common.cancel')}
                                      </Button>
                                    </div>
                                  )
                                : (
                                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setConfirmingId(m.id)}>
                                      {t('organization.member.remove')}
                                    </Button>
                                  )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default OrganizationMembersModal
